import { create } from 'zustand';

import type { TenantStatus } from '../modules/tenant/types';

export interface UserProfile {
  id: string;
  /** Avatar trong thanh điều hướng lấy chữ đầu: "Nguyễn Văn An" → "N". */
  fullName: string;
  /**
   * Email nhận thư (claim `email`, ADR-0101): nơi Keycloak và nền tảng gửi thư.
   * Có thể khác tên đăng nhập.
   */
  email: string;
  /**
   * Tên đăng nhập (claim `preferred_username`, ADR-0101). Vắng thì trùng `email`
   * (token không có claim, tài khoản mẫu cũ).
   */
  tenDangNhap?: string;
  /** Phần tử đầu tiên hiện dưới tên trong thanh điều hướng. */
  roles: string[];
  /** Chuỗi dạng "MODULE.ACTION". */
  permissions: string[];
  /**
   * Dịch vụ người này được dùng (ADR-0019 mục 2.1), tính từ `me/context`.
   *
   * Chỉ có ở tài khoản cổng tenant đăng nhập thật. Để trống — tài khoản mẫu,
   * nhân sự nền tảng — thì thanh điều hướng và `PermissionGuard` không lọc
   * theo gói (`lib/dich-vu-cong.ts`).
   */
  dichVu?: string[];
  /**
   * Quyền lớp 3 nguyên văn từ `me/context` (`van-ban:template:create`…), đã cắt
   * theo gói. Chỉ có ở tài khoản cổng tenant đăng nhập thật; module so khớp
   * đúng mã này ở backend, nên giao diện đọc nó để hiện nút ghi
   * (`useModulePermission`).
   */
  quyenModule?: string[];
  /**
   * Trạng thái tenant của tài khoản cổng tenant đăng nhập thật, từ
   * `me/context` (`tenant_status`). Để trống ở tài khoản mẫu và nhân sự nền
   * tảng: không có băng báo tổ chức tạm ngưng.
   */
  trangThaiTenant?: TenantStatus;
  /**
   * Role lớp 1 nguyên văn từ token (`realm_access.roles`). Chỉ có khi đăng
   * nhập Keycloak; `roles` ở trên đã là khoá nhãn nên mất tên role gốc.
   */
  vaiTroLop1?: string[];
  organizationId?: string;
  organizationIds?: string[];
  /**
   * Tenant của **tài khoản mẫu** ở cổng tenant, để dữ liệu mẫu theo tenant
   * (bảo mật, nhật ký) có nội dung. Đăng nhập thật thì để trống: tenant lấy từ
   * `GET /users/me/context` qua `useTenantCuaToi`, không đọc ở đây.
   */
  tenantId?: string;
}

interface AuthState {
  user: UserProfile | null;
  setUser: (user: UserProfile | null) => void;
  logout: () => void;
}

/**
 * Kho trạng thái đăng nhập.
 *
 * Đăng xuất ở đây luôn **chỉ trong phạm vi ứng dụng này**: nó kết thúc phiên
 * của riêng cổng này, không đăng xuất người dùng khỏi các sản phẩm FoxAI khác,
 * dù realm định danh là dùng chung (§10.16).
 */
export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  setUser: (user) => set({ user }),
  logout: () => set({ user: null }),
}));
